/**
 * OpenAI client construction and the model catalog.
 *
 * The chat half of this file is gone: sending, streaming, and the hand-rolled
 * SSE parser it needed all live in features/chat now, over the SDK's own stream.
 * What remains is what the non-chat tabs still need — a configured client, the
 * catalog fetch, and the settings→params mapping the text tab hands its
 * transport.
 */

import OpenAI from 'openai';

/**
 * Authentication configuration for OpenAI client
 */
export interface OpenAIAuthConfig {
  token: string;
  isApiKey: boolean;
}

/**
 * Creates an OpenAI client instance configured for the chat API
 *
 * @param authConfig - Authentication configuration (bearer token or API key)
 * @returns Configured OpenAI client instance
 */
export function createOpenAIClient(authConfig: OpenAIAuthConfig): OpenAI {
  const baseURL = import.meta.env.VITE_CHAT_API_BASE_URL;

  if (!baseURL) {
    throw new Error('VITE_CHAT_API_BASE_URL is not configured');
  }

  // The OpenAI SDK always uses the apiKey parameter as "Authorization: Bearer <apiKey>"
  // If your backend expects a different header format (like X-API-Key),
  // you'll need to configure your backend to accept Bearer tokens
  // or use a proxy/middleware to transform the headers

  return new OpenAI({
    apiKey: authConfig.token, // SDK will format as "Authorization: Bearer <token>"
    baseURL,
    dangerouslyAllowBrowser: true,
  });
}

const normalizeBaseUrl = (baseURL: string): string => {
  return baseURL.endsWith('/') ? baseURL : `${baseURL}/`;
};

/**
 * Convert a ChatCompletionSettings object into the options shape expected by
 * sendChatMessage. Parses the JSON fields (jsonSchema, tools) safely; drops
 * invalid JSON. Only emits fields the user actually set.
 */
export function chatSettingsToOptions(settings: {
  temperature: number;
  maxTokens: number | null;
  topP: number;
  frequencyPenalty: number;
  presencePenalty: number;
  seed: number | null;
  stop: string[];
  responseFormat: 'text' | 'json_object' | 'json_schema';
  jsonSchema: string;
  tools: string;
  reasoningEffort: 'low' | 'medium' | 'high' | null;
  thinkingBudget: number | null;
}): {
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
  frequency_penalty?: number;
  presence_penalty?: number;
  seed?: number;
  stop?: string[];
  response_format?: { type: string; json_schema?: unknown };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tools?: any[];
  reasoning_effort?: 'low' | 'medium' | 'high';
  thinking_budget?: number;
} {
  const opts: {
    temperature?: number;
    max_tokens?: number;
    top_p?: number;
    frequency_penalty?: number;
    presence_penalty?: number;
    seed?: number;
    stop?: string[];
    response_format?: { type: string; json_schema?: unknown };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tools?: any[];
    reasoning_effort?: 'low' | 'medium' | 'high';
    thinking_budget?: number;
  } = {
    temperature: settings.temperature,
    max_tokens: settings.maxTokens ?? undefined,
    top_p: settings.topP,
    frequency_penalty: settings.frequencyPenalty,
    presence_penalty: settings.presencePenalty,
  };
  if (settings.seed !== null) opts.seed = settings.seed;
  if (settings.stop.length) opts.stop = settings.stop;
  if (settings.reasoningEffort) opts.reasoning_effort = settings.reasoningEffort;
  if (settings.thinkingBudget !== null) opts.thinking_budget = settings.thinkingBudget;
  if (settings.responseFormat === 'json_object') {
    opts.response_format = { type: 'json_object' };
  } else if (settings.responseFormat === 'json_schema' && settings.jsonSchema.trim()) {
    try {
      opts.response_format = {
        type: 'json_schema',
        json_schema: JSON.parse(settings.jsonSchema),
      };
    } catch {
      /* invalid JSON — omit */
    }
  }
  if (settings.tools.trim()) {
    try {
      const parsed = JSON.parse(settings.tools);
      if (Array.isArray(parsed) && parsed.length) opts.tools = parsed;
    } catch {
      /* invalid JSON — omit */
    }
  }
  return opts;
}

/**
 * Raw model entry returned by the gateway's /v1/models endpoint.
 * Extends the standard OpenAI Model type with a capabilities array
 * that the playground uses to filter models into tabs.
 */
export interface PricingTier {
  up_to_tokens: number;
  input_per_1m: number;
  output_per_1m: number;
}

export interface RawModelEntry {
  id: string;
  object: string;
  created: number;
  owned_by: string;
  capabilities?: string[];
  model_task?: string;
  input_modalities?: string[];
  output_modalities?: string[];
  input_cost_1m?: number;
  output_cost_1m?: number;
  pricing_tiers?: PricingTier[];
  per_image_cost?: number;
  per_second_cost?: number;
  per_audio_minute_cost?: number;
  per_char_cost?: number;
  audio_input_cost_1m?: number;
  metadata?: Record<string, unknown>;
}

/**
 * Fetches available models from the OpenAI-compatible API.
 *
 * Uses a raw fetch instead of the SDK's typed client.models.list() so that
 * the extra `capabilities` field returned by the gateway is preserved
 * (the SDK strips unknown fields).
 *
 * @param client - OpenAI client instance (used only for baseURL and auth)
 * @returns Promise resolving to array of available models with capabilities
 */
export async function fetchAvailableModels(client: OpenAI): Promise<RawModelEntry[]> {
  // Extract baseURL and apiKey from the client instance.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const c = client as any;
  const baseURL: string = c.baseURL || c._options?.baseURL || import.meta.env.VITE_CHAT_API_BASE_URL;
  const apiKey: string = c.apiKey || c._options?.apiKey || '';

  const url = new URL('models', normalizeBaseUrl(baseURL));

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication failed: Invalid or expired credentials');
    }
    if (response.status === 403) {
      throw new Error('Access forbidden: Insufficient permissions');
    }
    throw new Error(`Failed to fetch models: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return (data.data ?? []) as RawModelEntry[];
}
